import { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft,
  Database,
  FileText,
  Link2,
  Loader2,
  RefreshCw,
  X,
  ChevronDown,
  Phone,
  Calendar,
  Home
} from 'lucide-react';
import { fetchPricePredictions } from '../../services/pricePredictionService';
import './PricePredictions.css';

const defaultFilters = {
  name: '',
  phoneNumber: '',
  userbaseId: ''
};

const formatJsonBlock = (value, fallbackText) => {
  if (!value) return fallbackText;
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return fallbackText;
  }
};

// Normalize phone numbers: remove all non-digits, keep last 10 digits
const normalizePhone = (phone) => {
  if (!phone) return 'N/A';
  const digitsOnly = phone.replace(/\D/g, '');
  if (!digitsOnly) return 'N/A';
  const last10 = digitsOnly.slice(-10);
  // Format as: XXX-XXX-XXXX
  return last10.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

// Render table for array of objects
const RenderTable = ({ data }) => {
  if (!Array.isArray(data) || data.length === 0) {
    return <div className="data-empty">No data available</div>;
  }

  // Check if it's an array of primitives
  const isSimpleArray = data.every(item => item === null || typeof item !== 'object');
  if (isSimpleArray) {
    return (
      <div className="data-simple-array" style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {data.map((item, idx) => (
          <div key={idx} className="simple-array-item" style={{ padding: '0.5rem', background: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
            {formatValue(item)}
          </div>
        ))}
      </div>
    );
  }

  // Get all unique keys from all objects
  const allKeys = [...new Set(data.flatMap(obj => Object.keys(obj || {})))];

  return (
    <div className="data-table-container">
      <table className="data-table">
        <thead>
          <tr>
            {allKeys.map((key) => (
              <th key={key} className={key.toLowerCase().includes('address') ? 'address-cell' : ''}>{key}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              {allKeys.map((key) => {
                const val = row[key];
                const isComplex = val !== null && typeof val === 'object';
                return (
                  <td key={`${idx}-${key}`} className={`data-cell ${key.toLowerCase().includes('address') ? 'address-cell' : ''}`}>
                    {isComplex ? (
                      <PropertyItemWithExpand itemKey={key} value={val} isTableContext={true} />
                    ) : (
                      formatValue(val)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Property item with expandable objects
const PropertyItemWithExpand = ({ itemKey, value, isTableContext }) => {
  const [expanded, setExpanded] = useState(false);
  const isComplex = value !== null && typeof value === 'object';

  if (isComplex) {
    const isArray = Array.isArray(value);
    const count = isArray ? value.length : Object.keys(value).length;
    const label = isArray ? (count === 1 ? 'item' : 'items') : (count === 1 ? 'field' : 'fields');

    return (
      <div key={itemKey}>
        <div className="property-item" style={isTableContext ? { gridTemplateColumns: '1fr', padding: 0, border: 'none', background: 'transparent' } : {}}>
          {!isTableContext && <span className="property-label">{itemKey}</span>}
          <button
            className="property-expand-btn"
            onClick={() => setExpanded(!expanded)}
            style={isTableContext ? { padding: '0.25rem 0.5rem', background: 'rgba(28,59,144,0.05)', border: '1px solid rgba(28,59,144,0.1)', borderRadius: '4px', width: '100%', justifyContent: 'flex-end', color: '#1C3B90' } : { width: '100%', justifyContent: 'flex-end' }}
          >
            {!expanded && <span className="property-object-info">({count} {label})</span>}
            <ChevronDown size={20} className={`expand-icon ${expanded ? 'open' : ''}`} />
          </button>
        </div>
        {expanded && (
          <div className="nested-object" style={isTableContext ? { marginTop: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' } : {}}>
            {isArray ? <RenderTable data={value} /> : <RenderObject data={value} />}
          </div>
        )}
      </div>
    );
  }

  return (
    <div key={itemKey} className="property-item" style={isTableContext ? { padding: 0, border: 'none', display: 'block' } : {}}>
      {!isTableContext && <span className="property-label">{itemKey}</span>}
      <span className="property-value">{formatValue(value)}</span>
    </div>
  );
};

// Render object as properties
const RenderObject = ({ data }) => {
  if (!data || typeof data !== 'object') {
    return <div className="data-empty">No data available</div>;
  }

  const entries = Object.entries(data);

  if (entries.length === 0) {
    return <div className="data-empty">No properties</div>;
  }

  return (
    <div className="data-properties">
      {entries.map(([key, value]) => (
        <PropertyItemWithExpand key={key} itemKey={key} value={value} />
      ))}
    </div>
  );
};

// Format individual values
const formatValue = (value) => {
  if (value === null || value === undefined) {
    return <span className="value-null">—</span>;
  }

  if (typeof value === 'boolean') {
    return <span className="value-boolean">{value ? 'Yes' : 'No'}</span>;
  }

  if (typeof value === 'number') {
    return <span className="value-number">{value.toLocaleString()}</span>;
  }

  if (typeof value === 'string') {
    return <span className="value-string">{value}</span>;
  }

  if (Array.isArray(value)) {
    const count = value.length;
    return <span className="value-array">({count} {count === 1 ? 'item' : 'items'})</span>;
  }

  if (typeof value === 'object') {
    const count = Object.keys(value).length;
    return <span className="value-object">({count} {count === 1 ? 'field' : 'fields'})</span>;
  }

  return <span>{String(value)}</span>;
};

const DataModal = ({ isOpen, title, data, onClose }) => {
  if (!isOpen) return null;

  // Determine content type and render accordingly
  const renderContent = () => {
    if (!data) {
      return <div className="data-empty">No data available</div>;
    }

    if (Array.isArray(data)) {
      return <RenderTable data={data} />;
    }

    if (typeof data === 'object') {
      return <RenderObject data={data} />;
    }

    return <div className="data-value">{String(data)}</div>;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <X />
          </button>
        </div>
        <div className="modal-body">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

const PricePredictions = ({ onBack }) => {
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [modalState, setModalState] = useState({ isOpen: false, title: '', data: null });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [debouncedFilters, setDebouncedFilters] = useState(defaultFilters);

  const loadPredictions = useCallback(async (targetPage = 1, activeFilters = defaultFilters) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchPricePredictions({
        ...activeFilters,
        page: targetPage,
        limit: 20
      });

      setRows(response.data || []);
      setPage(response.currentPage || targetPage);
      setTotalPages(Math.max(response.totalPages || 1, 1));
      setTotal(response.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch prediction data.');
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce filters - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters]);

  // Reload data when debounced filters change (this also inherently handles the initial load)
  useEffect(() => {
    setPage(1);
    loadPredictions(1, debouncedFilters);
  }, [debouncedFilters, loadPredictions]);

  const handleClear = () => {
    setFilters(defaultFilters);
    setPage(1);
    loadPredictions(1, defaultFilters);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    loadPredictions(nextPage, filters);
  };

  const openModal = (title, data) => {
    setModalState({ isOpen: true, title, data });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, title: '', data: null });
  };

  return (
    <div className="price-predictions-page">
      <header className="price-predictions-header">
        <div>
          <button className="predictions-back-btn" onClick={onBack}>
            <ArrowLeft />
            <span>Back To Dashboard</span>
          </button>
          <h1>AI Price Predictions</h1>
          <p>Filter by name, phone number, and userbase ID.</p>
        </div>
        <button className="predictions-refresh-btn" onClick={() => loadPredictions(page, filters)}>
          <RefreshCw />
          <span>Refresh</span>
        </button>
      </header>

      <section className="predictions-filters-section">
        <div className="predictions-filter-grid">
          <div className="predictions-filter-group">
            <label>Name</label>
            <input
              type="text"
              value={filters.name}
              onChange={(event) => setFilters((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Search username"
            />
          </div>
          <div className="predictions-filter-group">
            <label>Phone Number</label>
            <input
              type="text"
              value={filters.phoneNumber}
              onChange={(event) => setFilters((prev) => ({ ...prev, phoneNumber: event.target.value }))}
              placeholder="Search phone number"
            />
          </div>
          <div className="predictions-filter-group">
            <label>Userbase ID</label>
            <input
              type="text"
              value={filters.userbaseId}
              onChange={(event) => setFilters((prev) => ({ ...prev, userbaseId: event.target.value }))}
              placeholder="Search userbase ID"
            />
          </div>
        </div>
        <div className="predictions-filter-actions">
          <button className="predictions-clear-btn" onClick={handleClear}>
            <X />
            <span>Clear</span>
          </button>
        </div>
      </section>

      <div className="predictions-summary">
        {loading ? 'Searching...' : `Total Results: ${total}`}
      </div>

      {loading ? (
        <div className="predictions-state loading">
          <Loader2 className="spin" />
          <p>Loading price prediction data...</p>
        </div>
      ) : error ? (
        <div className="predictions-state error">
          <p>{error}</p>
          <button onClick={() => loadPredictions(page, filters)}>Try Again</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="predictions-state empty">
          <p>No prediction data found for the selected filters.</p>
        </div>
      ) : (
        <div className="predictions-grid">
          {rows.map((row) => (
            <article className="prediction-card" key={row._id || `${row.predictionId}-${row.phoneNumber}`}>
              <div className="prediction-card-header">
                <div className="prediction-header-content">
                  <div className="prediction-username-date">
                    <h2 className="prediction-username">{row.username || 'Unknown User'}</h2>
                    <span className="prediction-date-badge">
                      <Calendar size={16} /> 
                      {formatDate(row.createdAt)}
                    </span>
                  </div>
                  
                  <div className="prediction-prominent-info">
                    <span className="prominent-phone">
                      <Phone size={18} /> 
                      {normalizePhone(row.phoneNumber)}
                    </span>
                  </div>

                  <div className="prediction-card-details">
                    <span><strong>Userbase ID:</strong> {row.userbaseId || '-'}</span>
                    <span><strong>Prediction ID:</strong> {row.predictionId || '-'}</span>
                    <span><strong>Language:</strong> {row.language || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="prediction-actions">
                <button
                  className="prediction-data-btn"
                  onClick={() => openModal('Property Data', row.propertyData)}
                >
                  <Database size={18} />
                  <span>Property Data</span>
                  <ChevronDown size={16} />
                </button>
                <button
                  className="prediction-data-btn"
                  onClick={() => openModal('Prediction Data', row.predictionData)}
                >
                  <FileText size={18} />
                  <span>Prediction Data</span>
                  <ChevronDown size={16} />
                </button>
                
                <div className="prediction-dropdown-container">
                  <button
                    className="prediction-data-btn"
                    onClick={() => setActiveDropdown(activeDropdown === row._id ? null : row._id)}
                  >
                    <Home size={18} />
                    <span>Similar Properties</span>
                    <ChevronDown size={16} className={`dropdown-icon ${activeDropdown === row._id ? 'open' : ''}`} />
                  </button>
                  
                  {activeDropdown === row._id && (
                    <div className="prediction-dropdown-menu">
                      {Array.isArray(row.similarProperties?.comparableSales) && row.similarProperties.comparableSales.length > 0 && (
                        <button 
                          className="prediction-dropdown-btn"
                          onClick={() => openModal('Similar Sales', row.similarProperties.comparableSales)}
                        >
                          <Home size={16} /> Similar Sales
                        </button>
                      )}
                      {Array.isArray(row.similarProperties?.comparableRentals) && row.similarProperties.comparableRentals.length > 0 && (
                        <button 
                          className="prediction-dropdown-btn"
                          onClick={() => openModal('Similar Rentals', row.similarProperties.comparableRentals)}
                        >
                          <Home size={16} /> Similar Rentals
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="prediction-reports">
                <h4>Reports</h4>
                {Array.isArray(row.reportUrls) && row.reportUrls.length > 0 ? (
                  <ul className="report-links">
                    {row.reportUrls.map((report, index) => (
                      <li key={`${report.url || ''}-${index}`}>
                        <a href={report.url} target="_blank" rel="noreferrer">
                          <Link2 size={14} />
                          {report.filename || report.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-reports-text">No reports generated</p>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="predictions-pagination">
          <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}>
            Next
          </button>
        </div>
      )}

      <DataModal
        isOpen={modalState.isOpen}
        title={modalState.title}
        data={modalState.data}
        onClose={closeModal}
      />
    </div>
  );
};

export default PricePredictions;
